import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@cordata/buffet';
import {
  Card,
  CardBody,
  CardFooter,
  Col,
  Table,
  Row,
  Alert,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { compact } from 'lodash';

import {
  formatDate,
  getFullName,
  adminInstance as Admin,
  publicInstance as Public,
  toastSuccess,
  toastError,
} from '~/utils';
import { MatchStatus, MatchType } from '~/utils/const/identity';
import CordataModal from '~/components/common/Modal';
import DataPoint from './DataPoint';

const DISPUTE_ACTIONS = [{
  id: MatchStatus.Approved,
  header: 'Approve Match',
  body: 'Match is confirmed.',
  label: 'Approve Match',
  color: 'success',
  icon: 'check-circle',
  visibleIn: [
    MatchStatus.NeedToBeApproved,
  ],
}, {
  id: MatchStatus.NeedToBeApproved,
  header: 'Ready For Approval',
  label: 'Ready For Approval',
  body: 'Match is confirmed and needs to be approved.',
  color: 'warning',
  icon: 'file-export',
  visibleIn: [
    MatchStatus.Disputed,
    MatchStatus.Unmatched,
    MatchStatus.OnHold,
  ],
}, {
  id: MatchStatus.OnHold,
  header: 'On Hold',
  label: 'Move Match to On Hold',
  body: 'Additional review is required.',
  color: 'tertiary',
  icon: 'hand-paper',
  visibleIn: [
    MatchStatus.Unmatched,
    MatchStatus.NeedToBeApproved,
  ],
}, {
  id: MatchStatus.Rejected,
  header: 'Reject Match',
  label: 'Reject Match',
  body: 'The ID(s) have been confirmed as a mismatch.',
  color: 'danger',
  icon: 'times',
  visibleIn: [
    MatchStatus.Disputed,
    MatchStatus.Unmatched,
    MatchStatus.NeedToBeApproved,
    MatchStatus.OnHold,
  ],
}];

type Props = {
  data: any;
  matchStatusId: typeof MatchStatus;
};

const EhrDataRow = ({
  data,
  matchStatusId,
}: Props) => {
  const history = useHistory();
  const [isMRNDisputedOnAnotherChild, setIsMRNDisputedOnAnotherChild] = useState(false);
  const [preppedAction, prepAction] = useState<typeof DISPUTE_ACTIONS[0]>();

  const isManual = (datum: typeof data) => datum.attributes.matchTypeId === MatchType.Manual;

  const algorithmUsesField = (datum: typeof data[0], fields: string[]) =>
    fields.every(f => !datum.attributes.fieldsdisqualifieddeterministicmatching?.includes(f));

  const confirmAction = () => {
    let payload = {
        matchStatusId: preppedAction?.id,
    };

    if (preppedAction?.id !== MatchStatus.OnHold) {
      payload["userModified"] = true
    }
    Admin
      .put(`/dispute-action/${data.attributes.tblMatchresultsUid}`, payload)
      .then(() => {
        toastSuccess();
        prepAction(undefined);
        history.goBack();
      })
      .catch(toastError);
  };

  useEffect(() => {
    Public
      .get('list_view_cs_tbl_matchresults', {
        params: {
          lookuppersonid: data.attributes.lookuppersonid,
          'sourcepersonid__!': data.attributes.sourcepersonid,
          sitename: data.attributes.sitename,
          'matchStatusId__!': MatchStatus.Rejected,
        },
      })
      .then(r => r.data.data)
      .then(r => setIsMRNDisputedOnAnotherChild(r?.length > 0));
  }, []);

  return (
    <Card>
      <CardBody className="my-3">
        <Row>
          <Col sm>
            <DataPoint
              label="Date Identified"
              value={formatDate(data.attributes.matchdate)}
            />

            <DataPoint
              label="Hospital"
              value={data.attributes.sourceDescription}
            />

            <DataPoint
              label="Algorithm Match Details"
              value={data.attributes.description || 'N/A'}
            />

            <DataPoint
              label="Reason for Dispute"
              value={data.attributes.disputeComment}
            />

            <DataPoint
              label="Dispute Date"
              value={formatDate(data.attributes.matchStatusDate)}
            />
          </Col>
        </Row>

        <Table size="sm" bordered>
          <tr>
            <th />
            <th>PCSA Data</th>
            <th>Used by Algorithm</th>
            <th>EHR</th>
          </tr>

          <tr>
            <th />

            <td>
              <div>Person ID: {data.attributes.sourcepersonid}</div>
              <div>Case ID: {data.attributes.caseId}</div>
            </td>

            <td />

            <td>{data.attributes.lookuppersonid}</td>
          </tr>

          <tr>
            <th>Name</th>

            <td>
              {getFullName(
                data.attributes.sourcepersonfirstname,
                data.attributes.sourcepersonlastname,
              )}
            </td>

            <td rowSpan={2} className="text-center">
              {isManual(data)
                ? 'N/A'
                : algorithmUsesField(data, ['FirstName', 'LastName', 'Alias'])
                  ? <FontAwesomeIcon icon="check" className="text-success" size="sm" />
                  : <FontAwesomeIcon icon="times" className="text-danger" size="sm" />
              }
            </td>

            <td rowSpan={2}>
              {isManual(data)
                ? <FontAwesomeIcon icon="clock" />
                : (
                  <>
                    {data.attributes.ehrAlias?.join('; ')}
                  </>
                )
              }
            </td>
          </tr>

          <tr>
            <th>Alias</th>

            <td>{compact([
              data.attributes.alias1,
              data.attributes.alias2,
              data.attributes.alias3,
            ])?.join('; ')}</td>
          </tr>

          <tr>
            <th>DoB</th>

            <td>
              {formatDate(data.attributes.sourcepersondateofbirth)}
            </td>

            <td className="text-center">
              {isManual(data)
                ? 'N/A'
                : algorithmUsesField(data, ['DateOfBirth'])
                  ? <FontAwesomeIcon icon="check" className="text-success" size="sm" />
                  : <FontAwesomeIcon icon="times" className="text-danger" size="sm" />
              }
            </td>

            <td>
              {isManual(data)
                ? <FontAwesomeIcon icon="clock" />
                : formatDate(data.attributes.lookuppersondateofbirth)
              }
            </td>
          </tr>

          <tr>
            <th>Gender</th>

            <td>{data.attributes.sourcepersongender}</td>

            <td className="text-center">
              {isManual(data)
                ? 'N/A'
                : algorithmUsesField(data, ['Gender'])
                  ? <FontAwesomeIcon icon="check" className="text-success" size="sm" />
                  : <FontAwesomeIcon icon="times" className="text-danger" size="sm" />
              }
            </td>

            <td>
              {isManual(data)
                ? <FontAwesomeIcon icon="clock" />
                : data.attributes.lookuppersongender
              }
            </td>
          </tr>

          <tr>
            <th>Race</th>

            <td>{data.attributes.race}</td>

            <td />

            <td>{data.attributes.ehrRaces?.join(', ')}</td>
          </tr>

          <tr>
            <th>Ethnicity</th>

            <td>{data.attributes.ethnicity}</td>

            <td />

            <td>{data.attributes.ehrEthnicities?.join(', ')}</td>
          </tr>

          <tr>
            <th>Associated Adult</th>

            <td>{data.attributes.caseName}</td>

            <td />

            <td>{data.attributes.ehrAssociatedAdults?.join('; ')}</td>
          </tr>
        </Table>

        {isMRNDisputedOnAnotherChild &&
          data.attributes.matchStatusId === MatchStatus.NeedToBeApproved &&
          matchStatusId === MatchStatus.NeedToBeApproved && (
          // the only time this alert is relevant is when the user could
          // possibly approve the match, which is only on NeedToBeApproved
          <Alert color="info">
            This MRN is paired with another SACWIS ID that is under{' '}
            review. Approving this match will remove the MRN from{' '}
            the other SACWIS ID.
          </Alert>
        )}
      </CardBody>

      {data.attributes.matchStatusId === matchStatusId && (
        <CardFooter className="mt-0">
          {DISPUTE_ACTIONS
            .filter(a => a.visibleIn.includes(data.attributes.matchStatusId))
            .map(a => (
              <Button
                color={a.color}
                onClick={() => {
                  prepAction(a);
                }}
                type="button"
                outline
              >
                {a.header}
              </Button>
            ))}
        </CardFooter>
      )}

      {preppedAction && (
        <CordataModal
          isOpen={preppedAction}
          headerIcon={preppedAction.icon}
          title={preppedAction.header}
          action={
            <>
              <Button outline type="cancel" onClick={() => prepAction(undefined)}>
                Cancel
              </Button>

              <Button
                type="button"
                color={preppedAction.color}
                onClick={confirmAction}
              >
                {preppedAction.label}
              </Button>
            </>
          }
        >
          {preppedAction.body}

          <br />

          {isMRNDisputedOnAnotherChild && (preppedAction.id === MatchStatus.Approved) && (
            <Alert color="danger">
              You are about to approve this MRN match to this SACWIS ID while it is{' '}
              under review with another SACWIS ID. Doing this will match this SACWIS ID{' '}
              to the MRN and remove the MRN from the other SACWIS ID.
            </Alert>
          )}
        </CordataModal>
      )}
    </Card>
  );
};

export default EhrDataRow;
